import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import rulePopout from 'components/triggers/RulePopout';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import UserContextSelect from 'components/triggers/UserContextSelect';
import UserStore from 'stores/UserStore';
import AuthStore from 'stores/AuthStore';
import UserActions from 'actions/UserActions';
import DepartmentStore from 'stores/department/DepartmentStore';
import TeamActions from 'actions/TeamActions';
import TeamListStore from 'stores/TeamListStore';
import FlatButton from 'components/pure/utils/FlatButton';
import {UserConditionType} from 'records/UserCondition';
import {FeedbackReviewerIsRecipient, FeedbackTriggerType} from 'records/FeedbackTrigger';
import {List} from 'immutable';
import keyBy from 'lodash/keyBy';

class UserContextSelectContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedContextType: null,
      selectedValue: null,
      selectedOption: null,
    };

    this.selectedContextTypeChanged = this.selectedContextTypeChanged.bind(this);
    this.selectedValueChanged = this.selectedValueChanged.bind(this);
    this.saveSelection = this.saveSelection.bind(this);
    this.updateSelection = this.updateSelection.bind(this);
    this.saveButtonClick = this.saveButtonClick.bind(this);
    this.lookupCondition = this.lookupCondition.bind(this);
  }

  static getStores() {
    return [UserStore, DepartmentStore, TeamListStore];
  }

  static getPropsFromStores() {
    const userOptions = UserStore.getState().userList.map(u => {
      return {label: u.getFullName(), value: u.id};
    });

    const departmentOptions = DepartmentStore.getState().departmentProfiles.map(d => {
      return {label: d.name, value: d.id};
    });

    const teamList = TeamListStore.getState()
      .departmentTeams.valueSeq()
      .filter(t => t !== null && t.size > 0);

    const teamOptions = teamList.flatMap(teams =>
      teams.toArray().map(t => {
        return {label: t.name, value: t.id};
      })
    );

    // I'm looping through department teams twice.  This just looks cleaner to me
    const managerOptions = teamList.flatMap(teams =>
      teams
        .toArray()
        .filter(t => t.manager || t.managerId)
        .map(t => {
          const id = t.manager ? t.manager.id : t.managerId;
          const foundUser = UserStore.getState().userList.find(u => u.id === id);
          const name = t.manager ? t.manager.name : foundUser && foundUser.getDisplayName();
          return {label: name, value: id};
        })
        .filter(i => i.label)
    );

    // dedupe the managerOptions list
    const uniqueManagers = new List(Object.values(keyBy(managerOptions.toArray(), i => i.value)));

    const roleOptions = new List([
      {label: 'Admin', value: 'admin'},
      {label: 'Manager', value: 'manager'},
      {label: 'User', value: 'user'},
    ]);

    return {
      userOptions: userOptions,
      departmentOptions: departmentOptions,
      teamOptions: teamOptions,
      managerOptions: uniqueManagers,
      roleOptions: roleOptions,
    };
  }

  lookupCondition(selectedContextType, selectedValue, selectedOption) {
    if (selectedContextType === FeedbackTriggerType.REVIEWER_IS_RECIPIENT) {
      return new FeedbackReviewerIsRecipient({negated: selectedValue});
    } else {
      return UserConditionType.getUserCondition(
        selectedContextType,
        selectedValue,
        selectedOption && selectedOption.label
      );
    }
  }
  updateSelection(selectedContextType, selectedValue, selectedOption) {
    if (this.props.updateSelection) {
      const userContext = this.lookupCondition(selectedContextType, selectedValue, selectedOption);
      this.props.updateSelection(userContext);
    }
  }

  saveSelection(selectedContextType, selectedValue, selectedOption) {
    if (this.props.saveSelection) {
      const userContext = this.lookupCondition(selectedContextType, selectedValue, selectedOption);
      this.props.saveSelection(userContext);
    }
  }

  componentDidMount() {
    const isAdmin = AuthStore.getState().user.isAdmin();
    // XXX may want to optimize, but previous attempt to check only size > 0 was problematic
    UserActions.fetchAll({viewable_only: true});
    if (TeamListStore.getState().departmentTeams.size === 0) {
      TeamActions.fetchAllTeams(isAdmin);
    }
    this.setState({
      selectedValue: this.props.selectedValue,
      selectedContextType: this.props.selectedContextType,
    });
  }

  selectedContextTypeChanged(context) {
    this.setState({
      selectedValue: null,
      selectedOption: null,
      selectedContextType: context,
    });
    if (
      this.props.closeOnSelect &&
      (context === UserConditionType.ANY_USER ||
        context === UserConditionType.SHARED_ME ||
        context === UserConditionType.SHARED)
    ) {
      this.saveSelection(context, null, null);
    } else {
      if (
        context === UserConditionType.ANY_USER ||
        context === UserConditionType.SHARED_ME ||
        context === UserConditionType.SHARED
      ) {
        this.updateSelection(context, null, null);
      }
    }
  }

  // optionally set context (if not null) along with value
  selectedValueChanged(value, option, newContext = null) {
    const context = newContext || this.state.selectedContextType;
    this.setState({
      selectedContextType: context,
      selectedValue: value,
      selectedOption: option,
    });
    if (this.props.closeOnSelect) {
      this.saveSelection(context, value, option);
    } else {
      this.updateSelection(context, value, option);
    }
  }

  saveButtonClick() {
    this.saveSelection(
      this.state.selectedContextType,
      this.state.selectedValue,
      this.state.selectedOption
    );
  }

  canSave() {
    const {selectedValue, selectedContextType} = this.state;
    return (
      selectedContextType === UserConditionType.ANY_USER ||
      selectedContextType === UserConditionType.SHARED_ME ||
      selectedContextType === UserConditionType.SHARED ||
      selectedValue != null
    );
  }

  render() {
    const {selectedValue, selectedContextType} = this.state;
    const canSave = this.canSave();
    return (
      <div className="rule-select-popup">
        <UserContextSelect
          {...this.props}
          selectedContextType={selectedContextType}
          selectedValue={selectedValue}
          selectedContextTypeChanged={this.selectedContextTypeChanged}
          selectedValueChanged={this.selectedValueChanged}
        />
        <div className="selection-buttons">
          <button disabled={!canSave} className="save" onClick={() => this.saveButtonClick()}>
            <CheckIcon color="white" />
          </button>
          <FlatButton onClick={() => this.props.closeRequest('cancel')}>Cancel</FlatButton>
        </div>
      </div>
    );
  }
}

export default rulePopout(connectToStores(UserContextSelectContainer));
